import {TableNode} from '@table-library/react-table-library'
import {useMemo} from 'react'
import {DataTableColumnInfo, DataTableProps} from '../model'

interface ReturnValue<ColumnID extends string, ITEM extends TableNode> {
  data: { nodes: Array<ITEM> },
  columns: Array<DataTableColumnInfo<ColumnID, ITEM>>,
}

interface Props<ColumnID extends string, ITEM extends TableNode> extends DataTableProps<ColumnID, ITEM> {
  searchValue: string,
}

export function useDataTableFilteredData<ColumnID extends string, ITEM extends TableNode>({
  data: initialData,
  columns: initialColumns,
  manageColumns,
  searchData,
  searchValue,
}: Props<ColumnID, ITEM>): ReturnValue<ColumnID, ITEM> {
  const visibleColumnIds = manageColumns?.visibleColumnIds
  const columns = useMemo(() => {
    if (!visibleColumnIds) {
      return initialColumns
    }
    const visibleColumnsSet = new Set(visibleColumnIds)
    return initialColumns.filter(x => visibleColumnsSet.has(x.id))
  }, [initialColumns, visibleColumnIds])

  const data = useMemo(() => (searchData && searchValue
    ? initialData.filter(x => searchData.searchFn(x, searchValue))
    : initialData), [initialData, searchData, searchValue])

  return useMemo(() => ({
    data: { nodes: data },
    columns,
  }), [columns, data])
}